import { ITractLayerConfig, normalizeRgba } from './MapConfig';

// Tracks rotating-palette assignments so the same value gets the same color
// across re-renders within one `getTract*Info()` call.
export type AutoAssignState = {
    nextIndex: number;
    assignments: Map<string, number[]>;
};

export function createAutoAssignState(): AutoAssignState {
    return { nextIndex: 0, assignments: new Map() };
}

function colorsEqual(a: number[], b: number[]): boolean {
    if (a.length < 3 || b.length < 3) {
        return false;
    }
    return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
}

// Returns a normalized (0–1 alpha) RGBA from `layerCfg.predefinedColors[value]` if present.
export function resolvePredefined(value: string, layerCfg: ITractLayerConfig | undefined): number[] | undefined {
    const raw = layerCfg?.predefinedColors?.[value];
    return normalizeRgba(raw);
}

// Resolves a color for `value` using:
//   1. layerCfg.predefinedColors[value]                                      (if present)
//   2. assignableList rotating palette (skipping any colors in `skipColors`) (if non-empty)
//   3. fallback                                                              (always)
//
// `state` persists assignments so the same value resolves to the same color
// every time within a single render pass.
export function resolveColor(
    value: string,
    layerCfg: ITractLayerConfig | undefined,
    assignableList: number[][] | undefined,
    state: AutoAssignState,
    fallback: number[],
    skipColors: number[][] = []
): number[] {
    const predefined = resolvePredefined(value, layerCfg);
    if (predefined) {
        return predefined;
    }

    const existing = state.assignments.get(value);
    if (existing) {
        return existing;
    }

    if (assignableList && assignableList.length > 0) {
        const filtered = assignableList
            .map((c) => normalizeRgba(c))
            .filter((c): c is number[] => !!c)
            .filter((c) => !skipColors.some((skip) => colorsEqual(c, skip)));

        if (filtered.length > 0) {
            const color = filtered[state.nextIndex % filtered.length];
            state.nextIndex += 1;
            state.assignments.set(value, color);
            return color;
        }
    }

    return fallback;
}



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/ColorResolver.ts